import React, { useCallback, useEffect, useState } from 'react'
import { ArrowPathIcon, CalendarDaysIcon, ChevronRightIcon, PlusCircleIcon, MinusCircleIcon } from '@heroicons/react/20/solid';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { getAssessmentDetailById, postAssessment, updateAssessmentDetailsById } from '../../api/assessmentquestionnaires.api';
import { getSectionsByQuestionnaire, createSectionsBulk, deleteSection } from '../../api/assessmentsection.api';

import { getCurrentFormattedDate, formats, toolbar } from '../../helpers/constants';
import { useNotification } from '../../context/NotificationContext';
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { AssessmentSection } from '../../Models';
import useApiToken from '../../hooks/useApiToken';

function AddEditAssessment() {
    const token = useApiToken();
    const [assessmentDetails, setAssessmentDetails] = useState({
        title: '',
        description: '',
        baseScore: '',
        time: '',
        exceptionalScore: '',
        numOfQuestions: '',
        lastUpdatedDate: getCurrentFormattedDate(),
        startTime: null as string | null,
        breakTime: '' as string | number | null,
        breakAfterNoQuestions: '' as string | number | null
    })
    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const [selectedTime, setSelectedTime] = useState<string | null>(null);

    const [postAssessmentDetails, setPostAssessmentDetails] = useState({
        title: '',
        description: '',
        baseScore: '',
        time: '',
        exceptionalScore: '',
        numOfQuestions: '',
        lastUpdatedDate: getCurrentFormattedDate(),
        startTime: null as string | null,
        breakTime: '' as string | number | null,
        breakAfterNoQuestions: '' as string | number | null
    })
    const { success } = useNotification();

    const [useSections, setUseSections] = useState(false);
    const [sections, setSections] = useState<{ id?: string; title: string; orderIndex: number; hasBreakAfter: boolean; numOfQuestions: number }[]>([]);
    const [deletedSectionIds, setDeletedSectionIds] = useState<string[]>([]);

    const { id } = useParams();

    const navigate = useNavigate();

    const handleChangeDate = (e: any) => {
        setSelectedDate(e.target.value);
    };

    const handleChangeTime = (e: any) => {
        setSelectedTime(e.target.value);
    };

    const getAssessmentDetails = useCallback(async () => {
        if (!token) return;
        if (id) {
            try {
                const res = await getAssessmentDetailById(id, token);
                setAssessmentDetails(res);
                setPostAssessmentDetails(res);
                if (res.startTime) {
                    const dateAndTime = res.startTime;
                    const date = dateAndTime.split("T")[0];
                    const time = dateAndTime.split("T")[1];
                    setSelectedTime(time)
                    setSelectedDate(date)
                }
            } catch (err) {
                console.log(err);
            }

            try {
                const res = await getSectionsByQuestionnaire(id, token);
                if (res && res.length > 0) {
                    setUseSections(true);
                    setSections(res.map((s: AssessmentSection) => ({
                        id: s.id,
                        title: s.title,
                        orderIndex: s.orderIndex,
                        hasBreakAfter: s.hasBreakAfter,
                        numOfQuestions: s.numOfQuestions || 0
                    })));
                }
            } catch (err) {
                console.log(err);
            }
        }
        else {
            setAssessmentDetails({
                title: '',
                description: '',
                baseScore: '',
                time: '',
                exceptionalScore: '',
                numOfQuestions: '',
                lastUpdatedDate: getCurrentFormattedDate(),
                startTime: null,
                breakTime: '',
                breakAfterNoQuestions: ''

            })
            setSections([]);
            setUseSections(false);
        }
    }, [id, token]);

    const handleAddSection = () => {
        setSections(prev => [...prev, {
            title: `Section ${prev.length + 1}`,
            orderIndex: prev.length,
            hasBreakAfter: true,
            numOfQuestions: 10
        }]);
    };

    const getTotalQuestionsFromSections = () => {
        return sections.reduce((sum, s) => sum + (s.numOfQuestions || 0), 0);
    };

    const handleRemoveSection = (index: number) => {
        const sectionToRemove = sections[index];
        if (sectionToRemove.id) {
            setDeletedSectionIds(prev => [...prev, sectionToRemove.id!]);
        }
        setSections(prev => prev.filter((_, i) => i !== index).map((s, i) => ({ ...s, orderIndex: i })));
    };

    const handleSectionChange = (index: number, field: string, value: any) => {
        setSections(prev => prev.map((s, i) => i === index ? { ...s, [field]: value } : s));
    };

    useEffect(() => {
        if (id) {
            getAssessmentDetails();
        }
    }, [id, getAssessmentDetails]);

    useEffect(() => {
        if (useSections) {
            const total = getTotalQuestionsFromSections();
            setAssessmentDetails(prev => ({ ...prev, numOfQuestions: total.toString() }));
        }
    }, [sections, useSections]);

    const handleAssessmentSubmit = async (e: any) => {
        e.preventDefault();
        if (!token) return;
        const dataToSubmit = {
            ...assessmentDetails,
            breakTime: assessmentDetails.breakTime === '' ? null : assessmentDetails.breakTime,
            breakAfterNoQuestions: assessmentDetails.breakAfterNoQuestions === '' ? null : assessmentDetails.breakAfterNoQuestions,
        };
        const res = await postAssessment(dataToSubmit, token);

        if (useSections && sections.length > 0 && res?.id) {
            const sectionsToCreate = sections.map((section, index) => ({
                title: section.title,
                description: '',
                orderIndex: index,
                hasBreakAfter: section.hasBreakAfter,
                numOfQuestions: section.numOfQuestions,
                questionnaireId: res.id
            }));
            await createSectionsBulk(sectionsToCreate, token);
        }

        success("Success", "Assessment Added Successfully");
        setTimeout(() => {
            navigate('/assessments');
        }, 2000)
    }

    const handleAssessmentUpdate = async (e: any) => {
        e.preventDefault();
        if (!token) return;
        const dataToSubmit = {
            ...assessmentDetails,
            breakTime: assessmentDetails.breakTime === '' ? null : assessmentDetails.breakTime,
            breakAfterNoQuestions: assessmentDetails.breakAfterNoQuestions === '' ? null : assessmentDetails.breakAfterNoQuestions,
        };
        await updateAssessmentDetailsById(dataToSubmit, token);

        for (const sectionId of deletedSectionIds) {
            await deleteSection(sectionId, token);
        }

        if (useSections && sections.length > 0 && id) {
            const newSections = sections.filter(s => !s.id);
            if (newSections.length > 0) {
                const sectionsToCreate = newSections.map((section, index) => ({
                    title: section.title,
                    description: '',
                    orderIndex: section.orderIndex,
                    hasBreakAfter: section.hasBreakAfter,
                    numOfQuestions: section.numOfQuestions,
                    questionnaireId: id
                }));
                await createSectionsBulk(sectionsToCreate, token);
            }
        }

        success("Success", "Assessment Edited Successfully");
        setTimeout(() => {
            navigate('/assessments');
        }, 2000)
    }

    const validate = () => {
        const { title, description, baseScore, time, exceptionalScore, numOfQuestions } = assessmentDetails;
        if (!title.trim() || !description.trim() || !(baseScore?.toString().length > 0) || !(time?.toString().length > 0) || !(exceptionalScore?.toString().length > 0)) {
            return true;
        }
        if (useSections) {
            if (sections.length === 0) return true;
            const totalQuestions = getTotalQuestionsFromSections();
            if (totalQuestions === 0) return true;
            for (const section of sections) {
                if (!section.title.trim() || section.numOfQuestions <= 0) return true;
            }
        } else {
            if (!(numOfQuestions?.toString().length > 0)) return true;
        }
        return false;
    }

    useEffect(() => {
        if (selectedDate && selectedTime) {
            const combinedDateTime = new Date(`${selectedDate}T${selectedTime}`).toISOString();

            setPostAssessmentDetails(prevDetails => ({
                ...prevDetails,
                startTime: combinedDateTime
            }));
            setAssessmentDetails(prevDetails => ({
                ...prevDetails,
                startTime: combinedDateTime
            }));
        }
    }, [selectedDate, selectedTime]);

    const handleResetTime = () => {
        setAssessmentDetails(prevDetails => ({
            ...prevDetails,
            startTime: null
        }));
        setPostAssessmentDetails(prevDetails => ({
            ...prevDetails,
            startTime: null
        }));
        setSelectedDate('');
        setSelectedTime('');
    };

    return (
        <div className=''>
            <header className="bg-gray-50 py-8 dark:bg-darkbglight">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 xl:flex xl:items-center xl:justify-between">
                    <div className="min-w-0 flex-1">
                        <nav className="flex" aria-label="Breadcrumb">
                            <ol className="flex items-center space-x-4">
                                <li>
                                    <div>
                                        <Link
                                            to="/assessments"
                                            className="text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                                        >
                                            Assessments
                                        </Link>
                                    </div>
                                </li>
                                <li>
                                    <div className="flex items-center">
                                        <ChevronRightIcon
                                            className="h-5 w-5 flex-shrink-0 text-gray-400 dark:text-gray-400"
                                            aria-hidden="true"
                                        />
                                        {
                                            id ? <span className="ml-4 text-sm font-medium text-gray-500 dark:text-gray-400">
                                                Edit
                                            </span>
                                                : <span className="ml-4 text-sm font-medium text-gray-500 dark:text-gray-400">
                                                    Create
                                                </span>
                                        }
                                    </div>
                                </li>
                            </ol>
                        </nav>
                        {
                            id ? <>
                                <div className='flex flex-row justify-between align-center text-center'>
                                    <div className=''>
                                        <h1 className='mt-2 text-2xl font-bold leading-7 text-gray-900 sm:text-4xl sm:tracking-tight  text-left dark:text-gray-300'>{postAssessmentDetails.title}</h1>
                                        <h4 className='mt-2 text-xs font-bold leading-7 text-gray-900 sm:text-sm sm:tracking-tight dark:text-gray-300 text-left'>Num of Questions: {postAssessmentDetails.numOfQuestions}</h4>
                                    </div>
                                    <div className='flex flex-col align-center justify-center'>
                                        <button className='text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-xs sm:text-base md:text-sm px-5 py-2.5 me-2 mb-2 dark:bg-blue-600 dark:hover:bg-blue-700 focus:outline-none dark:focus:ring-blue-800' onClick={() => navigate(`/assessment/questions/${id}`)}>
                                            Assessment Questions
                                        </button>

                                    </div>
                                </div>
                            </> : <h1 className="mt-2 text-2xl font-bold leading-7 text-gray-900 sm:text-4xl sm:tracking-tight  text-left dark:text-gray-300">
                                Create New Assessment
                            </h1>
                        }
                    </div>
                </div>
            </header>
            <form className="space-y-12 sm:space-y-16 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 ">
                <div className="">
                    <div className='flex flex-col items-start py-10 border-b border-gray-300 lg:flex-row dark:border-gray-600'>
                        <label htmlFor="assessment-title" className="block text-sm font-medium text-gray-700 w-64 text-left mb-4 dark:text-gray-400">
                            Title:
                        </label>
                        <input
                            id="assessment-title"
                            name="assessment-title"
                            type="text"
                            className="w-full rounded-md border-gray-300 border p-2 focus:outline-none focus:ring focus:border-indigo-500 dark:bg-transparent dark:text-gray-400 dark:border-gray-600"
                            onChange={(e) => setAssessmentDetails({ ...assessmentDetails, title: e.target.value })}
                            value={assessmentDetails.title}
                            placeholder='Eg. Sr. Software Engineer'
                        />

                    </div>
                    <div className='flex flex-col items-start py-10 border-b border-gray-300 lg:flex-row dark:border-gray-600'>
                        <label htmlFor="assessment-desc" className="block text-sm font-medium text-gray-700 w-64 text-left	mb-4 dark:text-gray-400">
                            Description:
                        </label>
                        <ReactQuill
                          value={assessmentDetails.description}
                          onChange={(e) =>{
                            if(!assessmentDetails.title) return
                            setAssessmentDetails({ ...assessmentDetails, description: e })}}
                          formats={formats}
                          modules={{ toolbar: {container: toolbar,}}}
                          className="w-full rounded-md p-2 focus:outline-none focus:ring focus:border-indigo-500 min-h-[60px] max-h-[auto] text-left dark:text-gray-400 dark:border-gray-600"
                          id="assessment-description"
                        />
                    </div>
                    <div className='flex flex-col items-start py-10 border-b border-gray-300 lg:flex-row dark:border-gray-600'>
                        <label htmlFor="base-scrore" className="block text-sm font-medium text-gray-700 w-64 text-left	mb-4 dark:text-gray-400">
                            Base Score:
                        </label>
                        <input
                            id="base-scrore"
                            name="base-scrore"
                            type="number"
                            className="w-full rounded-md border-gray-300 border p-2 focus:outline-none focus:ring focus:border-indigo-500 dark:bg-transparent dark:text-gray-400 dark:border-gray-600"
                            onChange={(e) => setAssessmentDetails({ ...assessmentDetails, baseScore: e.target.value })} value={assessmentDetails.baseScore}
                            placeholder='Eg. 60'

                        />
                    </div>

                    <div className='flex flex-col items-start py-10 border-b border-gray-300 lg:flex-row dark:border-gray-600'>
                        <label htmlFor="questionTime" className="block text-sm font-medium text-gray-700 w-64 text-left	mb-4 dark:text-gray-400">
                            Time <small>(In minutes)</small> :
                        </label>
                        <input
                            id="questionTime"
                            name="questionTime"
                            type="number"
                            className="w-full rounded-md border-gray-300 border p-2 focus:outline-none focus:ring focus:border-indigo-500 dark:bg-transparent dark:text-gray-400 dark:border-gray-600"
                            onChange={(e) => setAssessmentDetails({ ...assessmentDetails, time: e.target.value })} value={assessmentDetails.time}
                            placeholder='Eg. 60'
                            min={0}

                        />
                    </div>
                    <div className='flex flex-col items-start py-10 border-b border-gray-300 lg:flex-row dark:border-gray-600'>
                        <label htmlFor="ex-score" className="block text-sm font-medium text-gray-700 w-64 text-left	mb-4 dark:text-gray-400">
                            Exceptional Score:
                        </label>
                        <input
                            id="ex-score"
                            name="ex-score"
                            type="number"
                            className="w-full rounded-md border-gray-300 border p-2 focus:outline-none focus:ring focus:border-indigo-500 dark:bg-transparent dark:text-gray-400 dark:border-gray-600"
                            onChange={(e) => setAssessmentDetails({ ...assessmentDetails, exceptionalScore: e.target.value })} value={assessmentDetails.exceptionalScore}
                            placeholder='Eg. 90'
                            min={0}
                        />
                    </div>
                    <div className='flex flex-col items-start py-10 border-b border-gray-300 lg:flex-row dark:border-gray-600'>
                        <label className="block text-sm font-medium text-gray-700 w-64 text-left mb-4 dark:text-gray-400">
                            Use Sections:
                        </label>
                        <div className="w-full">
                            <input
                                type="checkbox"
                                className="form-checkbox h-5 w-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                checked={useSections}
                                onChange={(e) => {
                                    setUseSections(e.target.checked);
                                    if (e.target.checked && sections.length === 0) {
                                        handleAddSection();
                                    }
                                    if (!e.target.checked) {
                                        setAssessmentDetails(prev => ({ ...prev, numOfQuestions: '' }));
                                    }
                                }}
                            />
                        </div>
                    </div>
                    {useSections && (
                        <div className='flex flex-col items-start py-10 border-b border-gray-300 dark:border-gray-600'>
                            <label className="block text-sm font-medium text-gray-700 w-64 text-left mb-4 dark:text-gray-400">
                                Sections:
                            </label>
                            <div className="w-full space-y-4">
                                {sections.map((section, index) => (
                                    <div key={index} className="flex flex-col gap-3 p-4 border border-gray-200 rounded-lg dark:border-gray-600">
                                        <div className="flex items-center gap-4">
                                            <input
                                                type="text"
                                                value={section.title}
                                                onChange={(e) => handleSectionChange(index, 'title', e.target.value)}
                                                className="flex-1 rounded-md border-gray-300 border p-2 focus:outline-none focus:ring focus:border-indigo-500 dark:bg-transparent dark:text-gray-400 dark:border-gray-600"
                                                placeholder="Section Title"
                                            />
                                            <MinusCircleIcon
                                                className="h-6 w-6 text-red-500 cursor-pointer hover:text-red-600"
                                                onClick={() => handleRemoveSection(index)}
                                            />
                                        </div>
                                        <div className="flex items-center gap-6">
                                            <div className="flex items-center gap-2">
                                                <label className="text-sm text-gray-600 dark:text-gray-400">No. of Questions:</label>
                                                <input
                                                    type="number"
                                                    value={section.numOfQuestions}
                                                    onChange={(e) => handleSectionChange(index, 'numOfQuestions', parseInt(e.target.value) || 0)}
                                                    className="w-20 rounded-md border-gray-300 border p-2 focus:outline-none focus:ring focus:border-indigo-500 dark:bg-transparent dark:text-gray-400 dark:border-gray-600"
                                                    min={1}
                                                />
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    checked={section.hasBreakAfter}
                                                    onChange={(e) => handleSectionChange(index, 'hasBreakAfter', e.target.checked)}
                                                    className="form-checkbox h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                                />
                                                <span className="text-sm text-gray-600 dark:text-gray-400">Break After</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                <div
                                    className="flex items-center gap-2 text-indigo-600 cursor-pointer hover:text-indigo-500"
                                    onClick={handleAddSection}
                                >
                                    <PlusCircleIcon className="h-5 w-5" />
                                    <span className="text-sm">Add Section</span>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className='flex flex-col items-start py-10 border-b border-gray-300 lg:flex-row dark:border-gray-600'>
                        <label htmlFor="noOfQues" className="block text-sm font-medium text-gray-700 w-64 text-left	mb-4 dark:text-gray-400">
                            Number of Questions:
                        </label>
                        <input
                            id="noOfQues"
                            name="noOfQues"
                            type="number"
                            className={`w-full rounded-md border-gray-300 border p-2 focus:outline-none focus:ring focus:border-indigo-500 dark:bg-transparent dark:text-gray-400 dark:border-gray-600 ${useSections ? 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed' : ''}`}
                            onChange={(e) => !useSections && setAssessmentDetails({ ...assessmentDetails, numOfQuestions: e.target.value })}
                            value={useSections ? getTotalQuestionsFromSections() : assessmentDetails.numOfQuestions}
                            placeholder='Eg. 30'
                            min={0}
                            readOnly={useSections}
                        />
                    </div>
                    <div className='flex flex-col items-start py-10 border-b border-gray-300 lg:flex-row dark:border-gray-600'>
                        <label htmlFor="startTime" className="block text-sm font-medium text-gray-700 w-64 text-left mb-4 dark:text-gray-400">
                            Start Time
                        </label>
                        <div className="flex items-center w-full gap-2">
                            <input
                                type="date"
                                onChange={handleChangeDate}
                                value={selectedDate || ''}
                                className='rounded-md border-gray-300 border p-2 focus:outline-none focus:ring focus:border-indigo-500 dark:bg-transparent dark:text-gray-400 dark:border-gray-600'
                            />
                            <input type="time" name="" id="" className=' rounded-md border-gray-300 border p-2 focus:outline-none focus:ring focus:border-indigo-500 dark:bg-transparent dark:text-gray-400 dark:border-gray-600 ' onChange={handleChangeTime}
                                value={selectedTime || ''}
                            />
                            <ArrowPathIcon height={18} className='dark:text-gray-200 text-gray-800 cursor-pointer' onClick={handleResetTime} />
                        </div>
                    </div>
                    <div className='flex flex-col items-start py-10 border-b border-gray-300 lg:flex-row dark:border-gray-600'>
                        <label htmlFor="breakTime" className="block text-sm font-medium text-gray-700 w-64 text-left mb-4 dark:text-gray-400">
                            Break Time <small>(In minutes)</small>:
                        </label>
                        <input
                            id="breakTime"
                            name="breakTime"
                            type="number"
                            className="w-full rounded-md border-gray-300 border p-2 focus:outline-none focus:ring focus:border-indigo-500 dark:bg-transparent dark:text-gray-400 dark:border-gray-600"
                            onChange={(e) => setAssessmentDetails({ ...assessmentDetails, breakTime: e.target.value ? parseInt(e.target.value) : null })} 
                            value={assessmentDetails.breakTime || ''}
                            placeholder='Eg. 5'
                            min={0}
                        />
                    </div>
                    {!useSections && (
                        <div className='flex flex-col items-start py-10 border-b border-gray-300 lg:flex-row dark:border-gray-600'>
                            <label htmlFor="breakAfterNoQuestions" className="block text-sm font-medium text-gray-700 w-64 text-left mb-4 dark:text-gray-400">
                                Break After Number of Questions:
                            </label>
                            <input
                                id="breakAfterNoQuestions"
                                name="breakAfterNoQuestions"
                                type="number"
                                className="w-full rounded-md border-gray-300 border p-2 focus:outline-none focus:ring focus:border-indigo-500 dark:bg-transparent dark:text-gray-400 dark:border-gray-600"
                                onChange={(e) => setAssessmentDetails({ ...assessmentDetails, breakAfterNoQuestions: e.target.value ? parseInt(e.target.value) : null })}
                                value={assessmentDetails.breakAfterNoQuestions || ''}
                                placeholder='Eg. 10'
                                min={0}
                            />
                        </div>
                    )}
                </div>

                <div className="mt-8 flex justify-end">
                    {
                        id ? <>
                            <button type="button" className="text-sm font-semibold text-gray-900 mr-4 dark:text-gray-100" onClick={getAssessmentDetails}>
                                Reset
                            </button>
                        </> : <>
                            <button type="button" className="text-sm font-semibold text-gray-900 mr-4 dark:text-gray-100" onClick={getAssessmentDetails}>
                                Clear
                            </button>
                        </>
                    }
                    {
                        id ? <button
                            onClick={handleAssessmentUpdate}
                            type="submit"
                            className={`rounded-md px-4 py-2 text-sm font-semibold shadow-sm focus:outline-none focus:ring focus:border-indigo-500 ${!validate() ? 'bg-indigo-600 text-white hover:bg-indigo-500 cursor-pointer'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                }`}
                            disabled={validate()}
                        >
                            Update
                        </button> :
                            <button
                                onClick={handleAssessmentSubmit}
                                type="submit"
                                className={`rounded-md px-4 py-2 text-sm font-semibold shadow-sm focus:outline-none focus:ring focus:border-indigo-500 ${!validate() ? 'bg-indigo-600 text-white hover:bg-indigo-500 cursor-pointer'
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    }`}
                                disabled={validate()}
                            >
                                Submit
                            </button>
                    }

                </div>
            </form>
        </div>
    )
}

export default AddEditAssessment
